import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, TrendingUp, ArrowDownToLine, ArrowUpFromLine, Clock, PlayCircle } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money, num } from "@/lib/format";

const Card = ({ icon: Icon, label, value, accent = "amber", testid }) => (
  <div data-testid={testid} className="rounded-xl bg-[#0D0E12] border border-amber-500/15 p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      <Icon className={`w-5 h-5 text-${accent}-400`} />
    </div>
    <div className="font-mono-fin text-2xl font-bold mt-3">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => toast.error("Load failed")); }, []);

  const runCycle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/run-income-cycle");
      toast.success(`Income cycle run for ${data.processed_users} users`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  if (!stats) return <div className="text-amber-400 font-mono-fin">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold">Dashboard Overview</h1>
        <button data-testid="run-cycle-btn" onClick={runCycle} disabled={busy} className="gold-btn px-5 py-3 rounded-xl font-bold text-sm flex items-center gap-2 disabled:opacity-60">
          <PlayCircle className="w-4 h-4" /> {busy ? "Running..." : "Run Income Cycle"}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card testid="admin-stat-users" icon={Users} label="Total Users" value={num(stats.total_users)} />
        <Card testid="admin-stat-invested" icon={TrendingUp} label="Total Invested" value={money(stats.total_invested)} accent="emerald" />
        <Card testid="admin-stat-deposited" icon={ArrowDownToLine} label="Total Deposited" value={money(stats.total_deposited)} accent="sky" />
        <Card testid="admin-stat-withdrawn" icon={ArrowUpFromLine} label="Total Withdrawn" value={money(stats.total_withdrawn)} accent="rose" />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-6 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Pending Deposits</div>
            <div className="font-mono-fin text-3xl font-bold text-amber-400 mt-1">{stats.pending_deposits}</div>
          </div>
          <Clock className="w-10 h-10 text-amber-500/40" />
        </div>
        <div className="glass rounded-2xl p-6 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Pending Withdrawals</div>
            <div className="font-mono-fin text-3xl font-bold text-amber-400 mt-1">{stats.pending_withdrawals}</div>
          </div>
          <Clock className="w-10 h-10 text-amber-500/40" />
        </div>
      </div>

      <div className="glass rounded-2xl p-6 text-sm text-slate-400">
        <strong className="text-amber-400">Total Investments:</strong> {num(stats.total_investments)} active/completed packages across the platform.
      </div>
    </div>
  );
}
